import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import gql from "graphql-tag";
import styled from "@emotion/styled";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { LoadingPage } from "../UI";
import { FlexMaxWidth } from "../UI/Containers";
import PageHeader from "../Page/PageHeader";
import { BusinessLink } from "../Business/BusinessList";
import { prefixImageUrl } from "../utils/imageUrl";

const Wrapper = styled("div")`
  margin-bottom: 2rem;
`;

const PageSection = styled("section")`
  line-height: 1.5em;
  &:nth-of-type(even) {
    background: ${(props) => props.theme.colors.accent};
  }
  padding: ${(props) => props.theme.spacing.default};

  ${(props) => props.theme.bp.below.lg} {
    padding: 2rem;
  }
  > div {
    padding: 0px 150px;
    ${(props) => props.theme.bp.below.lg} {
      padding: 0px;
    }
  }
`;

const Sidebar = styled("aside")`
  max-width: 16rem;
  width: 100%;
  margin-left: 2rem;
  flex-grow: 1;
  ${(props) => props.theme.bp.below.lg} {
    max-width: 100%;
    margin-left: 0;
  }
  h2 {
    margin-bottom: 0.5rem;
  }
  a,
  span {
    display: block;
  }
  img {
    margin: 1.5rem 0;
    max-width: 100%;
    height: auto;
    max-height: 120px;
    max-width: 120px;
  }
  & > div {
    margin-bottom: 2rem;
  }
`;

// const ContactPerson = (props) => (
//   <div>
//     <h2>{props.name}</h2>

//     {props.email && <a href={"mailto:" + props.email}>{props.email}</a>}
//     {props.phone && <span>Telefon: {props.phone}</span>}
//     {props.mobile && <span>Mobil: {props.mobile}</span>}
//   </div>
// );

export const Address = (props) => (
  <div style={{ marginBottom: "1rem" }}>
    <h2 className="subTitle">Adress</h2>

    {props.address && props.address}
    <br />
    {`${props.zipCode} ${props.city}`}
    {props.mobile && <span>Mobil: {props.mobile}</span>}
  </div>
);

export const Other = (props) => (
  <div>
    {props.space > 0 && (
      <>
        <strong>Yta:</strong>
        &nbsp;{props.space}m<sup>2</sup>
        <br />
      </>
    )}
    {typeof props.elevator == "boolean" && (
      <>
        <strong>Hiss:</strong>
        &nbsp;{props.elevator ? "Ja" : "Nej"} <br />
      </>
    )}
    {props.year && (
      <>
        <strong>Byggår:</strong>
        &nbsp;{parseInt(props.year)}
        <br />
      </>
    )}
    {props.renovated && (
      <>
        <strong>Renovering:</strong>
        &nbsp;{props.renovated}
        <br />
      </>
    )}
    {props.service && (
      <>
        <strong>Fastighetsskötare:</strong>
        &nbsp;{props.service}
        <br />
      </>
    )}
  </div>
);

const SideCol = ({ property }) => (
  <Sidebar>
    {/* {property.contact && <ContactPerson {...property.contact} />} */}
    {property.address && <Address {...property} />}
    <Other {...property} />
    {property.logo.url && <img src={prefixImageUrl(property.logo.url)} alt={property.title} />}
    {property.website !== null && (
      <BusinessLink
        static="1"
        inline="1"
        rel="noopener noreferrer"
        target="_blank"
        href={property.website}
      >
        {property.website
          .replace("http://", "")
          .replace("https://", "")
          .replace("www.", "")}
      </BusinessLink>
    )}
  </Sidebar>
);

const PageContent = (props) => {
  const property = props.data?.property;
  const longContent = property?.content?.length > 300;

  useEffect(() => {
    const categoryClass = property?.category?.toLowerCase() || 'default';
    document.body.className = categoryClass;
    return () => {
      document.body.className = 'default';
    };
  }, [property?.category]);

  if (!property) {
    return null;
  }

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{property.title}</title>
      </Helmet>
      <Wrapper>
        {property.image?.url ? <PageHeader propertyData={property} propertyContent={property.content} longContent={longContent} pageData={{ ...property, image: { ...property.image, url: prefixImageUrl(property.image.url) } }} /> : null}
        {longContent && (
          <React.Fragment>
            <PageSection>
              <FlexMaxWidth>
                <div
                  dangerouslySetInnerHTML={{
                    __html: property.content,
                  }}
                />
                <SideCol property={property} />
              </FlexMaxWidth>
            </PageSection>
          </React.Fragment>
        )}
      </Wrapper>
    </div>
  );
};

const BusinessPage = () => {
  const { slug } = useParams();

  const { loading, error, data } = useQuery(query, {
    variables: { slug },
  });

  if (loading) return <LoadingPage />;
  if (error) {
    console.error("Error fetching property:", error);
    return <p>Error loading page.</p>;
  }

  return <PageContent data={data} />;
};

const query = gql`
  query Property($slug: String) {
    property(slug: $slug) {
      title
      content
      excerpt
      website
      category
      image {
        url
      }
      logo {
        url
      }
      images {
        image {
          url
        }
      }
      contact {
        name
        email
        phone
        mobile
      }
      address
      zipCode
      city
      space
      elevator
      year
      renovated
      service
      category
    }
  }
`;

export default BusinessPage;
